import {
  Component, EventEmitter, OnDestroy, OnInit,
  Output
} from '@angular/core';
import {
  filter,
  map, Observable, Subject, take, takeUntil, BehaviorSubject
} from 'rxjs';
import {
  AbstractControl,
  FormControl, FormGroup, Validators
} from '@angular/forms';
import { ReferenceDataHttpService, ReferenceDatum } from '@sondermind/data-access/reference-data';
import {
  IQuickLeadIntakeForm,
  QuickLeadIntakeFormValues
} from '@sondermind/features/quick-intake';
import { DropdownOption } from '@sondermind/iris/dropdown';
import { PaymentTypes } from '@sondermind/features/public-facing/session-request';
import { SharedMasks } from '@sondermind/utilities/text-masks';
import { SimpleDateValidator } from '@sondermind/utilities/tools/validators/simple-date.validator';
import { EmailValidator } from '@sondermind/utilities/tools/validators/email.validator';
import { PhoneValidator } from '@sondermind/utilities/tools/validators/phone.validator';
import { PhoneAsyncValidator } from '@sondermind/data-access/phone-validator';
import { RadioOption } from '@sondermind/iris/radio-group';
import { TextareaSizes } from '@sondermind/iris/text-area';

import { SegmentService } from '@sondermind/segment';
import { BirthdayValidator } from '@sondermind/utilities/forms';

import { InfoLinks } from '@sondermind/core/enums/info-links.enum';

import { FormRefData } from '../../../models/enums/form-ref-data.enum';
import { QuickLeadIntakeFormState } from '../../../models/enums/quick-lead-intake-form-state.enum';
import {
  IQuickLeadIntakeSegmentSchema, IQuickLeadFormChangedData, QuickLeadIntakeSegmentEvents
} from '../../../models/segment/quick-lead-intake-segment-schema.interface';
import { QuickLeadIntakeStore } from '../../../stores/quick-lead-intake.store';
import { PaymentLocationService } from '../../../services/payment-location.service';
import { FormVersion } from '../../../models/enums/form-version.enum';

type QuickLeadIntakeFormKeys = keyof IQuickLeadIntakeForm;

@Component({
  selector: 'quick-lead-intake-form',
  templateUrl: './quick-lead-intake-form.component.html',
  styleUrls: ['./quick-lead-intake-form.component.scss'],
  providers: [PaymentLocationService],
  standalone: false
})
export class QuickLeadIntakeFormComponent implements OnInit, OnDestroy {
  constructor(
    private refDataHttp: ReferenceDataHttpService,
    private phoneAsyncValidator: PhoneAsyncValidator,
    private quickLeadIntakeStore: QuickLeadIntakeStore,
    private segmentService: SegmentService<IQuickLeadIntakeSegmentSchema>,
    private paymentLocationService: PaymentLocationService
  ) {
    this.setupForm();
  }

  @Output() formStateChange = new EventEmitter<QuickLeadIntakeFormState>();
  private formStateSubject = new BehaviorSubject<QuickLeadIntakeFormState>(QuickLeadIntakeFormState.FORM);
  get currentFormState(): QuickLeadIntakeFormState {
    return this.formStateSubject.value;
  }

  acceptedInsurances: Array<DropdownOption<number>> = [];
  paymentTypesInState: Array<DropdownOption<string>> = [];
  genderTypes: Array<RadioOption<number>> = [];
  readinessOptions: Array<RadioOption<string>> = [];
  sessionPreferenceTypes: Array<RadioOption<string | null>> = [];
  availableInYourState: boolean = true;
  showInsurancePlans: boolean = false;

  paymentTypes = PaymentTypes;
  textareaSizes = TextareaSizes;
  quickLeadIntakeFormState = QuickLeadIntakeFormState;

  comingToYourStateLink = InfoLinks.CLIENT_COMING_TO_YOUR_STATE;
  birthdayMask = SharedMasks['birthdayMask'].ngxMask;
  phoneMask = SharedMasks['phoneMask'].ngxMask;
  quickLeadIntakeForm!: FormGroup<IQuickLeadIntakeForm>;

  private destroyed$ = new Subject<void>();

  ngOnInit(): void {
    this.fireFormStartedSegmentEvent();
    this.subscribeToFormStateChange();
    this.setupFormChangedSubscriptions();
    this.loadGenders();
    this.loadSessionPreferences();
    this.loadReadiness();
    this.setupPaymentLocationSubscription();
    this.subscribeToMedicationCheckbox();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  setupForm(): void {
    this.quickLeadIntakeForm = new FormGroup<IQuickLeadIntakeForm>({
      openPreferences: new FormControl<string | null>(null, [Validators.required]),
      location: this.paymentLocationService.locationControl,
      paymentType: this.paymentLocationService.paymentTypeControl,
      insurance: this.paymentLocationService.insuranceControl,
      firstName: new FormControl<string | null>(null, [Validators.required]),
      lastName: new FormControl<string | null>(null, [Validators.required]),
      birthdate: new FormControl<string | null>(null, [
        Validators.required,
        SimpleDateValidator.format,
        BirthdayValidator.validate
      ]),
      email: new FormControl<string | null>(null, [Validators.required, EmailValidator.format]),
      phone: new FormControl<string | null>(
        null,
        {
          validators: [Validators.required, PhoneValidator.format],
          asyncValidators: [this.phoneAsyncValidator.validate.bind(this.phoneAsyncValidator)]
        }
      ),
      genderPreference: new FormControl<number | null>(null),
      sessionFormatPreference: new FormControl<string | null>(null),
      contactSmsAllowed: new FormControl<boolean | null>(true),
      commPrefs: new FormGroup({
        commPrefsPhone: new FormControl<boolean | null>(true),
        commPrefsSms: new FormControl<boolean | null>(true)
      }, Validators.required),
      medicationManagement: new FormControl<boolean>(false, { nonNullable: true }),
      readiness: new FormControl<string | null>(null, [Validators.required])
    });
  }

  loadRefDataObservable(dataType: FormRefData, skipOptionList: string[] = []): Observable<ReferenceDatum[]> {
    const skipOptions = skipOptionList.map((o) => o.toLowerCase());

    return this.refDataHttp.list(dataType,
      {
        filter:
          [{ key: 'hidden', value: 'false' }],
        sort: [{ key: 'order_number', asc: true }]
      }
    ).pipe(
      map((e) => e.data.filter((o) => !skipOptions.includes(o.title.toLowerCase())))
    );
  }

  loadGenders(): void {
    const skipOption = ['prefer not to answer'];
    this.loadRefDataObservable(FormRefData.GENDER, skipOption).pipe(
      map((data) => data.map((option) => ({
        name: option.title, value: option.id
      })))
    ).subscribe({
      next: (genderTypes) => {
        this.genderTypes = genderTypes;
      }
    });
  }

  loadSessionPreferences(): void {
    this.sessionPreferenceTypes = [
      { name: 'Virtual only', value: 'video' },
      { name: 'In-person only', value: 'inPerson' },
      { name: 'Both', value: null }
    ];
  }

  loadReadiness(): void {
    this.readinessOptions = [
      { name: 'I am ready to find a therapist and schedule a session.', value: 'action' },
      { name: 'I am just starting to explore my options.', value: 'contemplation' }
    ];
  }

  locationSelected(selectedAddress: google.maps.places.PlaceResult): void {
    this.paymentLocationService.locationSelected(selectedAddress);
  }

  setupPaymentLocationSubscription(): void {
    this.paymentLocationService.getAvailableInYourState$().pipe(
      takeUntil(this.destroyed$)
    ).subscribe((val) => {
      this.availableInYourState = val;
    });

    this.paymentLocationService.getPaymentTypesInState$().pipe(
      takeUntil(this.destroyed$)
    ).subscribe((val) => {
      this.paymentTypesInState = val;
    });

    this.paymentLocationService.getAcceptedInsurances$().pipe(
      takeUntil(this.destroyed$)
    ).subscribe((val) => {
      this.acceptedInsurances = val;
    });

    this.paymentLocationService.getShowInsurancePlans$().pipe(
      takeUntil(this.destroyed$)
    ).subscribe((val) => {
      this.showInsurancePlans = val;
    });
  }

  subscribeToMedicationCheckbox(): void {
    this.quickLeadIntakeForm.controls.medicationManagement.valueChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((value) => {
      this.paymentLocationService.setMedicationManagement(value);
    });
  }

  formSubmitted(): void {
    this.formStateSubject.next(QuickLeadIntakeFormState.PROCESSING);
    this.fireFormSubmittedSegmentEvent();
    this.quickLeadIntakeStore.submitQuickLeadIntake$(
      this.quickLeadIntakeForm.value as QuickLeadIntakeFormValues
    ).subscribe({
      next: () => {
        this.formStateSubject.next(QuickLeadIntakeFormState.SUBMITTED);
      },
      error: () => {
        this.formStateSubject.next(QuickLeadIntakeFormState.ERROR);
      }
    });
  }

  backToForm(): void {
    this.formStateSubject.next(QuickLeadIntakeFormState.FORM);
  }

  subscribeToFormStateChange(): void {
    this.formStateSubject.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((state) => {
      this.formStateChange.emit(state);
    });
  }

  setupFormChangedSubscriptions(): void {
    Object.keys(this.quickLeadIntakeForm.controls).forEach((key) => {
      this.setupControlSubscription(key, this.quickLeadIntakeForm.get(key));
    });
  }

  transformFieldValueForSegment(fieldName: string, value: unknown): string | boolean | undefined {
    switch (fieldName as QuickLeadIntakeFormKeys) {
      case 'insurance':
        return this.acceptedInsurances.find((x) => x.value === value)?.name;
      case 'genderPreference':
        return this.genderTypes.find((x) => x.value === value)?.name;
      case 'paymentType':
      case 'sessionFormatPreference':
      case 'contactSmsAllowed':
      case 'commPrefs':
      case 'medicationManagement':
      case 'readiness':
        return value as string | boolean;
      default:
        return undefined;
    }
  }

  setupControlSubscription(key: string, control: AbstractControl | null): void {
    if (!control) return;

    const isNestedGroup = control instanceof FormGroup && key !== 'location';
    const controls: Array<[string, AbstractControl]> = isNestedGroup
      ? Object.entries(control.controls)
      : [[key, control]];

    controls.forEach(([controlKey, ctrl]: [string, AbstractControl]) => {
      ctrl.valueChanges.pipe(
        filter((x) => !(x === null || x === undefined)),
        take(1),
        takeUntil(this.destroyed$)
      ).subscribe({
        next: (val) => {
          const eventData: IQuickLeadFormChangedData = { fieldName: controlKey, formVersion: FormVersion.ONE_PAGE };

          const transformedValue = this.transformFieldValueForSegment(key, val);
          if (transformedValue !== undefined) {
            eventData.value = transformedValue;
          }

          this.sendSegmentEvent(
            QuickLeadIntakeSegmentEvents.QUICK_LEAD_FORM_CHANGED, eventData
          );
        },
      });
    });
  }

  fireFormStartedSegmentEvent(): void {
    this.sendSegmentEvent(
      QuickLeadIntakeSegmentEvents.QUICK_LEAD_FORM_STARTED,
      { formVersion: FormVersion.ONE_PAGE }
    );
  }

  fireFormSubmittedSegmentEvent(): void {
    this.sendSegmentEvent(
      QuickLeadIntakeSegmentEvents.QUICK_LEAD_FORM_SUBMITTED,
      { formVersion: FormVersion.ONE_PAGE }
    );
  }

  private sendSegmentEvent<T extends keyof IQuickLeadIntakeSegmentSchema>(
    segmentEventType: T, data: IQuickLeadIntakeSegmentSchema[T]
  ): void {
    this.segmentService.track<T>(segmentEventType, data).catch(() => { });
  }
}
